import { ApiResponse } from '@interfaces/index';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

// ============================================================================
// API Handler
// ============================================================================

export const communityPostsApiHandler = {
  /**
   * Get community posts with pagination (supports lazy loading)
   * @param payload - Pagination and filtering options
   * @returns Paginated list of community posts
   */
  getCommunityPosts: async (
    payload: GetCommunityPostsPayload,
  ): Promise<ApiResponse<CommunityPostsResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post('/community/posts', payload),

  /**
   * Get replies for a specific post
   * @param postId - ID of the post
   * @param status - Optional status filter (for admin use)
   * @returns List of replies for the post
   */
  getPostReplies: async (
    postId: number,
    status?: PostStatus,
  ): Promise<ApiResponse<PostRepliesResponse>> => {
    const queryParam = status ? `?status=${status}` : '';

    return createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).get(
      `/community/posts/${postId}/replies${queryParam}`,
    );
  },

  /**
   * Get a single community post by id (email deep links / dedicated post page)
   * @param postId - ID of the post
   * @returns Community post object
   */
  getCommunityPost: async (postId: number): Promise<ApiResponse<CommunityPost>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).get(`/community/posts/${postId}`),

  /**
   * Create a new community post
   * @param payload - Post content
   * @returns Created post object
   */
  createPost: async (payload: CreatePostPayload): Promise<ApiResponse<CommunityPost>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post('/community/posts/create', payload),

  /**
   * Create a reply to a post
   * @param postId - ID of the post to reply to
   * @param payload - Reply content
   * @returns Created reply object
   */
  createReply: async (
    postId: number,
    payload: CreateReplyPayload,
  ): Promise<ApiResponse<CommunityReply>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(
      `/community/posts/${postId}/replies`,
      payload,
    ),

  /**
   * Update a community post
   * @param postId - ID of the post to update
   * @param payload - Updated post content
   * @returns Updated post object
   */
  updatePost: async (
    postId: number,
    payload: UpdatePostPayload,
  ): Promise<ApiResponse<CommunityPost>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).put(`/community/posts/${postId}`, payload),

  /**
   * Delete a community post (soft delete)
   * @param postId - ID of the post to delete
   * @returns Success message
   */
  deletePost: async (postId: number): Promise<ApiResponse<{ message: string }>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).delete(`/community/posts/${postId}`),

  /**
   * Update a community reply
   * @param replyId - ID of the reply to update
   * @param payload - Updated reply content
   * @returns Updated reply object
   */
  updateReply: async (
    replyId: number,
    payload: UpdateReplyPayload,
  ): Promise<ApiResponse<CommunityReply>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).put(`/community/replies/${replyId}`, payload),

  /**
   * Delete a community reply (soft delete)
   * @param replyId - ID of the reply to delete
   * @returns Success message
   */
  deleteReply: async (replyId: number): Promise<ApiResponse<{ message: string }>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).delete(`/community/replies/${replyId}`),

  /**
   * Toggle like on a post (like/unlike)
   * @param postId - ID of the post to like/unlike
   * @returns Updated like status and count
   */
  toggleLikePost: async (
    postId: number,
  ): Promise<ApiResponse<{ isLiked: boolean; likesCount: number }>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(`/community/posts/${postId}/like`),

  /**
   * Toggle like on a reply (like/unlike)
   * @param replyId - ID of the reply to like/unlike
   * @returns Updated like status and count
   */
  toggleLikeReply: async (
    replyId: number,
  ): Promise<ApiResponse<{ isLiked: boolean; likesCount: number }>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(`/community/replies/${replyId}/like`),

  /**
   * Get community posts and replies for moderation (admin only)
   * @param status - Filter by status: "pending", "active", or "deleted"
   * @param page - Page number (0-indexed, default: 0)
   * @param perPage - Items per page (default: 20)
   * @returns Paginated list of community posts and replies
   */
  getModerationItems: async (
    status: ModerationStatus,
    page = 0,
    perPage = 10,
  ): Promise<ApiResponse<ModerationResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).get('/community/moderation', {
      params: {
        status,
        page,
        perPage,
      },
    }),

  /**
   * Approve a pending post
   * @param postId - ID of the post to approve
   * @returns Updated post object
   */
  approvePost: async (postId: number): Promise<ApiResponse<CommunityPost>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(`/community/posts/${postId}/approve`),

  /**
   * Approve a pending reply
   * @param replyId - ID of the reply to approve
   * @returns Updated reply object
   */
  approveReply: async (replyId: number): Promise<ApiResponse<CommunityReply>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(`/community/replies/${replyId}/approve`),

  /**
   * Restore a deleted post
   * @param postId - ID of the post to restore
   * @returns Updated post object
   */
  restorePost: async (postId: number): Promise<ApiResponse<CommunityPost>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(`/community/posts/${postId}/restore`),

  /**
   * Restore a deleted reply
   * @param replyId - ID of the reply to restore
   * @returns Updated reply object
   */
  restoreReply: async (replyId: number): Promise<ApiResponse<CommunityReply>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V1).post(`/community/replies/${replyId}/restore`),
};

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export type PostStatus = 'pending' | 'active' | 'deleted';
export type ModerationStatus = 'pending' | 'active' | 'deleted';
export type ItemType = 'post' | 'reply';

export interface GetCommunityPostsPayload {
  page?: number; // Page number (0-indexed, default: 0)
  perPage?: number; // Items per page (default: 10)
  status?: PostStatus; // Optional: Filter by status (default: "active")
}

export interface CreatePostPayload {
  content: string; // 1-5000 characters
}

export interface CreateReplyPayload {
  content: string; // 1-5000 characters
}

export interface UpdatePostPayload {
  content: string; // 1-5000 characters
}

export interface UpdateReplyPayload {
  content: string; // 1-5000 characters
}

export interface CommunityPost {
  id: number;
  content: string;
  postedBy: string; // Formatted display name (e.g., "Someone in Police Force")
  timestamp: string; // ISO 8601 datetime
  likesCount: number;
  repliesCount: number;
  isLiked: boolean; // Whether current user liked this post
  isOwner: boolean; // Whether current user owns this post (for edit/delete)
  status: PostStatus; // "pending", "active", or "deleted"
  createdAt: string;
  updatedAt: string;
}

export interface CommunityReply {
  id: number;
  content: string;
  postedBy: string; // Formatted display name
  timestamp: string; // ISO 8601 datetime
  likesCount: number;
  isLiked: boolean; // Whether current user liked this reply
  isOwner: boolean; // Whether current user owns this reply (for edit/delete)
  parentPostId: number; // ID of the parent post
  status: PostStatus; // "pending", "active", or "deleted"
  createdAt: string;
  updatedAt: string;
}

export interface ModerationItem {
  id: number;
  type: ItemType; // "post" or "reply"
  content: string;
  postedBy: string;
  timestamp: string;
  likesCount: number;
  repliesCount?: number; // Only for posts
  parentPostId?: number; // Only for replies
  status: ModerationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationData {
  total: number; // Total number of items
  lastPage: number; // Last page number (0-indexed)
  prevPage: number | null; // Previous page number or null
  nextPage: number | null; // Next page number or null
  perPage: number; // Items per page
  currentPage: number; // Current page number (0-indexed)
  from: number; // Starting item number (1-indexed)
  to: number; // Ending item number (1-indexed)
}

export interface CommunityPostsResponse {
  data: CommunityPost[];
  pagination: PaginationData;
}

export interface PostRepliesResponse {
  data: CommunityReply[];
}

export interface ModerationResponse {
  data: ModerationItem[];
  pagination: PaginationData;
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to get community posts with regular pagination
 * Use this for initial load or when you want to control pagination manually
 */
export const useGetCommunityPosts = (
  payload: GetCommunityPostsPayload = {},
  options: {
    enabled?: boolean;
    refetchInterval?: number;
  } = {},
) =>
  useQuery({
    queryKey: ['communityPosts', payload.page ?? 0, payload.perPage ?? 10, payload.status],
    queryFn: () =>
      communityPostsApiHandler.getCommunityPosts({
        page: payload.page ?? 0,
        perPage: payload.perPage ?? 10,
        status: payload.status,
      }),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval,
  });

/**
 * Hook to get community posts with infinite scroll/lazy loading
 * Use this for scroll-based pagination (recommended for mobile/feed interfaces)
 *
 * Example:
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetCommunityPostsInfinite();
 */
export const useGetCommunityPostsInfinite = (
  payload: Omit<GetCommunityPostsPayload, 'page'> = {},
  options: {
    enabled?: boolean;
    refetchInterval?: number;
  } = {},
) =>
  useInfiniteQuery({
    queryKey: ['communityPostsInfinite', payload.perPage ?? 10, payload.status],
    queryFn: ({ pageParam = 0 }) =>
      communityPostsApiHandler.getCommunityPosts({
        page: pageParam,
        perPage: payload.perPage ?? 10,
        status: payload.status,
      }),
    getNextPageParam: (lastPage) => lastPage.responseObject?.pagination.nextPage ?? undefined,
    initialPageParam: 0,
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchInterval,
  });

/**
 * Hook to get a single community post by id
 */
export const useGetCommunityPost = (
  postId: number,
  options?: {
    enabled?: boolean;
  },
) =>
  useQuery({
    queryKey: ['communityPost', postId],
    queryFn: () => communityPostsApiHandler.getCommunityPost(postId),
    enabled: (options?.enabled ?? true) && Number.isFinite(postId) && postId > 0,
  });

/**
 * Hook to get replies for a specific post
 * Replies are NOT paginated - all replies are returned at once
 */
export const useGetPostReplies = (
  postId: number,
  status?: PostStatus,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  },
) =>
  useQuery({
    queryKey: ['postReplies', postId, status],
    queryFn: () => communityPostsApiHandler.getPostReplies(postId, status),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  });

/**
 * Hook to create a new community post
 * Automatically invalidates the posts query cache on success
 */
export const useCreatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.createPost,
    onSuccess: (response) => {
      // Invalidate all community posts queries to refetch with new post
      queryClient.invalidateQueries({ queryKey: ['communityPosts'] });
      queryClient.invalidateQueries({ queryKey: ['communityPostsInfinite'] });

      return response;
    },
  });
};

/**
 * Hook to create a reply to a post
 * Automatically invalidates the replies query cache and updates the post's reply count
 */
export const useCreateReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, content }: { postId: number; content: string }) =>
      communityPostsApiHandler.createReply(postId, { content }),
    onSuccess: (response, variables) => {
      // Invalidate replies query for this specific post
      queryClient.invalidateQueries({ queryKey: ['postReplies', variables.postId] });

      // Invalidate posts queries to update reply count
      queryClient.invalidateQueries({ queryKey: ['communityPosts'] });
      queryClient.invalidateQueries({ queryKey: ['communityPostsInfinite'] });
      queryClient.invalidateQueries({ queryKey: ['communityPost', variables.postId] });

      return response;
    },
  });
};

/**
 * Hook to update a community post
 * Automatically invalidates the posts query cache on success
 */
export const useUpdatePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, content }: { postId: number; content: string }) =>
      communityPostsApiHandler.updatePost(postId, { content }),
    onSuccess: (_response, variables) => {
      // Invalidate all community posts queries to refetch with updated post
      queryClient.invalidateQueries({ queryKey: ['communityPosts'] });
      queryClient.invalidateQueries({ queryKey: ['communityPostsInfinite'] });
      queryClient.invalidateQueries({ queryKey: ['communityPost', variables.postId] });
    },
  });
};

/**
 * Hook to delete a community post
 * Automatically invalidates the posts query cache on success
 */
export const useDeletePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.deletePost,
    onSuccess: (_response, postId) => {
      // Invalidate all community posts queries to refetch without deleted post
      queryClient.invalidateQueries({ queryKey: ['communityPosts'] });
      queryClient.invalidateQueries({ queryKey: ['communityPostsInfinite'] });
      queryClient.invalidateQueries({ queryKey: ['communityPost', postId] });
    },
  });
};

/**
 * Hook to update a community reply
 * Automatically invalidates the replies query cache on success
 */
export const useUpdateReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ replyId, content }: { replyId: number; content: string }) =>
      communityPostsApiHandler.updateReply(replyId, { content }),
    onSuccess: (response) => {
      // Invalidate replies query for the parent post
      const parentPostId = response.responseObject?.parentPostId;

      if (parentPostId) {
        queryClient.invalidateQueries({ queryKey: ['postReplies', parentPostId] });
      }
    },
  });
};

/**
 * Hook to delete a community reply
 * Automatically invalidates the replies query cache on success
 */
export const useDeleteReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.deleteReply,
    onSuccess: (response, replyId) => {
      // Invalidate all replies queries
      queryClient.invalidateQueries({ queryKey: ['postReplies'] });

      // Invalidate posts queries to update reply count
      queryClient.invalidateQueries({ queryKey: ['communityPosts'] });
      queryClient.invalidateQueries({ queryKey: ['communityPostsInfinite'] });
    },
  });
};

/**
 * Hook to toggle like on a post (like/unlike)
 * This is a toggle - same endpoint handles both like and unlike
 */
export const useToggleLikePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.toggleLikePost,
    onSuccess: (response, postId) => {
      // Optionally invalidate posts to sync like count across all instances
      // But relying on optimistic updates in components is more efficient
      // queryClient.invalidateQueries({ queryKey: ['communityPosts'] });
      // queryClient.invalidateQueries({ queryKey: ['communityPostsInfinite'] });
    },
  });
};

/**
 * Hook to toggle like on a reply (like/unlike)
 * This is a toggle - same endpoint handles both like and unlike
 */
export const useToggleLikeReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.toggleLikeReply,
    onSuccess: (response, replyId) => {
      // Optionally invalidate replies to sync like count
      // But relying on optimistic updates in components is more efficient
      // const parentPostId = response.responseObject?.parentPostId;
      // if (parentPostId) {
      //   queryClient.invalidateQueries({ queryKey: ['postReplies', parentPostId] });
      // }
    },
  });
};

/**
 * Hook to get moderation items (posts and replies) with pagination
 */
export const useGetModerationItems = (
  status: ModerationStatus,
  page = 0,
  perPage = 10,
  options: {
    enabled?: boolean;
  } = {},
) =>
  useQuery({
    queryKey: ['communityModeration', status, page, perPage],
    queryFn: () => communityPostsApiHandler.getModerationItems(status, page, perPage),
    enabled: options.enabled ?? true,
  });

/**
 * Hook to approve a pending post
 */
export const useApprovePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.approvePost,
    onSuccess: () => {
      // Invalidate all moderation queries to refetch
      queryClient.invalidateQueries({ queryKey: ['communityModeration'] });
    },
  });
};

/**
 * Hook to approve a pending reply
 */
export const useApproveReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.approveReply,
    onSuccess: () => {
      // Invalidate all moderation queries to refetch
      queryClient.invalidateQueries({ queryKey: ['communityModeration'] });
    },
  });
};

/**
 * Hook to restore a deleted post
 */
export const useRestorePost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.restorePost,
    onSuccess: () => {
      // Invalidate all moderation queries to refetch
      queryClient.invalidateQueries({ queryKey: ['communityModeration'] });
    },
  });
};

/**
 * Hook to restore a deleted reply
 */
export const useRestoreReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: communityPostsApiHandler.restoreReply,
    onSuccess: () => {
      // Invalidate all moderation queries to refetch
      queryClient.invalidateQueries({ queryKey: ['communityModeration'] });
    },
  });
};
